import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from "react-toastify";
import axios from "../../api/axios_client";
import './ManagementPages.css';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { useTranslation } from 'react-i18next';

const ReportsManagement = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total: 0,
    byCountry: {},
    byType: {}
  });

  const tabs = ['Monitoring', 'Prediction', 'Land Degradation'];
  
  const countries = [
    "Algeria", "Bahrain", "Comoros", "Djibouti", "Egypt", "Iraq", "Jordan",
    "Kuwait", "Lebanon", "Libya", "Mauritania", "Morocco", "Oman", "Palestine",
    "Qatar", "Saudi Arabia", "Somalia", "Sudan", "Syria", "Tunisia",
    "United Arab Emirates", "Yemen"
  ];

  const fetchReports = async () => {
    if (!selectedCountry) {
      setReports([]);
      setStats({
        total: 0,
        byCountry: {},
        byType: {}
      });
      return;
    }

    setLoading(true);
    try {
      const tabType = tabs[currentTab] === 'Land Degradation' ? 'landdegradation' : tabs[currentTab].toLowerCase();
      
      const response = await axios.get('/admin-panel', {
        params: {
          tab: 'reports',
          source: tabType,
          country: selectedCountry
        }
      });

      if (response.status !== 200) throw new Error('Network response was not ok');
      const data = response.data;

      const formattedReports = data.map(report => ({
        id: report.id,
        name: report.name,
        country: report.country,
        source: report.source,
        created_at: report.created_at,
        user: report.user,
        approved: report.approved
      }));

      setReports(formattedReports);
      
      const stats = {
        total: formattedReports.length,
        byCountry: {},
        byType: {}
      };

      formattedReports.forEach(report => {
        stats.byCountry[report.country] = (stats.byCountry[report.country] || 0) + 1;
        stats.byType[report.source] = (stats.byType[report.source] || 0) + 1;
      });

      setStats(stats);
    } catch (error) {
      toast.error('Failed to fetch reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`/reports/${reportToDelete.id}`);
      if (response.status !== 200) throw new Error('Delete failed');
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete report';
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setDeleteLoading(false); 
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  const handleApprovalToggle = (report) => {
    // Placeholder for future implementation
    toast.info(`Approval toggle for ${report.name} - soon!`);
  };

  useEffect(() => {
    fetchReports();
  }, [currentTab, selectedCountry]);

  const filteredReports = reports.filter(report => 
    (!searchTerm || report.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const metrics = [
    {
      title: "Total Reports",
      value: stats.total,
      change: "+5.2%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
    },
    {
      title: "Countries Coverage",
      value: Object.keys(stats.byCountry).length,
      change: "+3.1%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
    },
    {
      title: "Report Types",
      value: Object.keys(stats.byType).length,
      change: "+2.4%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
    },
  ];

  return (
    <div className="management-page">
      <div className="management-header">
        <h1>Reports Management</h1>
        <p>Manage and monitor all reports across the platform</p>
      </div>

      <Box sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          centered
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab) => (
            <Tab key={tab} label={tab} />
          ))}
        </Tabs>
      </Box>

      <div className="filters-section">
        <Box display="flex" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('select_country')}</InputLabel>
            <Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              label={t('select_country')}
            >
              <MenuItem value="">Select a country</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country} value={country}>{country}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            placeholder={t('search_reports')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />
        </Box>
      </div>

      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className="metric-icon-wrapper">
              {metric.icon}
            </div>
            <div className="metric-content">
              <h3>{metric.title}</h3>
              <div className="metric-value">
                <span className="number">{metric.value}</span>
                <span className={`change ${metric.trend}`}>{metric.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!selectedCountry ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography color="textSecondary">Please select a country to view reports</Typography>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="items-grid">
          {filteredReports.map((report) => (
            <Card key={report.id} className="admin-item-card">
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>
                <Box sx={{ p: 2.5, flex: 1 }}>
                  {/* Header with name */}
                  <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                    {report.name}
                  </Typography>

                  {/* Country and Type */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Country:</strong> {report.country}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Type:</strong> {report.source}
                    </Typography>
                  </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: '1px solid #eee' }} />

                {/* Bottom section - aligned to bottom */}
                <Box sx={{ p: 2.5, pt: 2 }}>
                  {/* Created date and user */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(report.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        User
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {report.user || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={report.approved ? 'warning' : 'success'}
                      startIcon={report.approved ? <CancelIcon /> : <CheckCircleIcon />}
                      onClick={() => handleApprovalToggle(report)}
                      sx={{ minWidth: 100 }}
                    >
                      {report.approved ? 'Disapprove' : 'Approve'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(report)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) { 
            setDeleteDialogOpen(false);
            setReportToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        itemName={reportToDelete?.name || 'this report'}
        isLoading={deleteLoading} 
      />
    </div>
  );
};

export default ReportsManagement;